import { useMutation } from '@apollo/client/react'
import { Button, Input, notification } from 'antd'
import { Formik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { UpdateCustomerEmailDocument } from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'

interface Props {
  userId: string
}

interface Values {
  email: string
}

const validationSchema = toFormikValidationSchema(
  z.object({
    email: z.string().min(1).email(),
  })
)

notification.config({ placement: 'bottomRight' })

const EmailChangeForm = ({ userId }: Props) => {
  const [updateCustomerEmail] = useMutation(UpdateCustomerEmailDocument)
  return (
    <Formik<Values>
      initialValues={{ email: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          const result = await updateCustomerEmail({
            variables: { email: values.email.toLowerCase(), id: userId },
          })
          if (!result) {
            return
          }
          const { data, error } = result
          if (error) {
            notification.error({
              message: 'Uh Oh',
              description: error.message,
            })
          } else if (data) {
            notification.success({
              message: 'Success!',
              description: `Email updated to ${data.updateCustomerEmail.email}`,
              duration: 0,
            })
            resetForm()
          }
        } catch (error) {
          notification.error({
            message: 'Uh Oh',
            description:
              error.message || 'Something went wrong, please try again',
          })
        }
        resetForm()
        setSubmitting(false)
      }}
    >
      {({
        handleChange,
        handleSubmit,
        isSubmitting,
        isValid,
        submitForm,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Input
            type="email"
            name="email"
            placeholder={i18n.t(
              'emailChangeForm.updateMailFormInputPlaceholder'
            )}
            value={values.email}
            onChange={handleChange}
            disabled={isSubmitting}
            size="large"
          />
          <Button
            type="primary"
            onClick={submitForm}
            disabled={!isValid || isSubmitting}
          >
            {i18n.t('emailChangeForm.updateMailFormButtonLabel')}
          </Button>
        </form>
      )}
    </Formik>
  )
}

export default EmailChangeForm
